/**
 * Viewer du journal d'audit — backoffice admin.
 *
 * Affichage : timeline paginée avec filtres (action, user, période).
 * Lecture seule (audit immutable côté BDD : pas d'UPDATE, pas de DELETE).
 *
 * Note RGPD : chaque consultation de cette page écrit elle-même une entrée
 * 'admin_audit_log_viewed' côté backend (traçabilité des accès DPO).
 *
 * Refonte R9 (mai 2026) :
 *   - Header avec LogoMark History accent tech
 *   - Alert info "consultation journalisée" sticky pour rappel RGPD
 *   - Card filtres glass avec form grid 4 cols (action, du, au, bouton)
 *   - Card table glass dense avec 5 colonnes
 *   - Pagination en boutons R2 secondary
 *
 * Logique métier 100% préservée :
 *   - api.admin.auditLog paginé (PAGE_SIZE 50) avec filtres action/from/to
 *   - ACTION_LABEL pour 22 types d'actions
 *   - COMMON_ACTIONS : 12 actions courantes dans le select
 *   - applyFilters : reset page à 1 + refetch
 *   - Conversion datetime-local → ISO pour from/to
 *   - Audit immutable affichage seul
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Card, Button, Input, Field, Alert, Badge, LogoMark, SectionEyebrow,
} from '@/components/ui';
import { api, ApiException } from '@/lib/api';
import { ChevronLeft, ChevronRight, History, AlertCircle, FileSpreadsheet } from 'lucide-react';
import type { AdminAuditLogItem, AuditAction } from '@roronoa-collectivites/shared-types';

const PAGE_SIZE = 50;

/** Libellé court par type d'action pour la timeline. */
const ACTION_LABEL: Partial<Record<AuditAction, string>> = {
  create: 'Création',
  update: 'Modification',
  delete: 'Suppression',
  login: 'Connexion',
  logout: 'Déconnexion',
  login_failed: 'Échec connexion',
  login_locked: 'Verrouillage compte',
  password_reset_requested: 'Demande reset mdp',
  password_reset_completed: 'Reset mdp consommé',
  email_verified: 'Email vérifié',
  email_verification_resent: 'Renvoi mail vérif',
  password_reset: 'Reset mdp (legacy)',
  role_changed: 'Changement de rôle',
  permission_granted: 'Permission accordée',
  permission_revoked: 'Permission révoquée',
  '2fa_enabled': '2FA activée',
  '2fa_disabled': '2FA désactivée',
  admin_user_updated: 'Admin · profil modifié',
  admin_user_suspended: 'Admin · compte suspendu',
  admin_user_reactivated: 'Admin · compte réactivé',
  admin_user_unlocked: 'Admin · compte déverrouillé',
  admin_user_force_password_reset: 'Admin · reset mdp forcé',
  admin_user_anonymized: 'Admin · anonymisation RGPD',
  admin_role_permissions_updated: 'Admin · permissions rôle modifiées',
  admin_audit_log_viewed: 'Admin · audit log consulté',
};

const COMMON_ACTIONS: AuditAction[] = [
  'login',
  'login_failed',
  'login_locked',
  'admin_user_updated',
  'admin_user_suspended',
  'admin_user_reactivated',
  'admin_user_unlocked',
  'admin_user_force_password_reset',
  'admin_user_anonymized',
  'admin_role_permissions_updated',
  'admin_audit_log_viewed',
  'role_changed',
];

const SELECT_CLS = 'h-10 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong';

export function AuditLogPage() {
  const [items, setItems] = useState<AdminAuditLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [actionFilter, setActionFilter] = useState<AuditAction | 'all'>('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const fetchLog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.admin.auditLog({
        page,
        pageSize: PAGE_SIZE,
        action: actionFilter === 'all' ? undefined : actionFilter,
        from: from ? new Date(from).toISOString() : undefined,
        to: to ? new Date(to).toISOString() : undefined,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (err) {
      if (err instanceof ApiException) {
        setError(err.payload.error || 'Erreur de chargement');
      } else {
        setError('Erreur réseau');
      }
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter, from, to]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const [exporting, setExporting] = useState(false);
  async function doExportExcel() {
    setExporting(true); setError(null);
    try {
      await api.admin.auditLogExportExcel({
        action: actionFilter === 'all' ? undefined : actionFilter,
        from: from ? new Date(from).toISOString() : undefined,
        to: to ? new Date(to).toISOString() : undefined,
      });
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Export impossible');
    } finally { setExporting(false); }
  }

  function applyFilters(e: React.FormEvent) {
    e.preventDefault();
    setPage(1);
    fetchLog();
  }

  return (
    <section aria-labelledby="audit-title" className="animate-fade-in">
      {/* En-tête de page */}
      <header className="mb-4 flex items-center gap-3">
        <LogoMark icon={History} accent="tech" size="lg" />
        <div>
          <SectionEyebrow as="div">Administration</SectionEyebrow>
          <h1
            id="audit-title"
            className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl"
          >
            Audit log
          </h1>
          <p className="mt-0.5 text-sm text-ink-muted">
            Journal immutable des actions sensibles. {total} entrée{total > 1 ? 's' : ''}.
          </p>
        </div>
        <Button
          variant="secondary" size="sm" className="ml-auto"
          onClick={() => void doExportExcel()}
          disabled={exporting}
        >
          <FileSpreadsheet className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">{exporting ? 'Export…' : 'Export Excel'}</span>
        </Button>
      </header>

      <Alert variant="info" icon={History} className="mb-4">
        Chaque consultation de cette page est elle-même journalisée (traçabilité RGPD / DPO).
      </Alert>

      {/* Filtres en Card glass */}
      <Card variant="glass" padded className="mb-4">
        <form
          onSubmit={applyFilters}
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
        >
          <Field label="Action" htmlFor="action-filter">
            <select
              id="action-filter"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value as AuditAction | 'all')}
              className={SELECT_CLS}
            >
              <option value="all">Toutes les actions</option>
              {COMMON_ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {ACTION_LABEL[a] ?? a}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Du" htmlFor="audit-from">
            <Input
              id="audit-from"
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </Field>
          <Field label="Au" htmlFor="audit-to">
            <Input
              id="audit-to"
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </Field>
          <div className="flex items-end">
            <Button type="submit" variant="primary" className="w-full">
              Filtrer
            </Button>
          </div>
        </form>
      </Card>

      {error && (
        <Alert variant="error" icon={AlertCircle} className="mb-4">
          {error}
        </Alert>
      )}

      <Card variant="glass" padded={false}>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="border-b border-border bg-surface/40 backdrop-blur-glass">
              <tr className="text-left">
                <th className="px-4 py-3 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Date</th>
                <th className="px-4 py-3 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Action</th>
                <th className="px-4 py-3 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Utilisateur</th>
                <th className="px-4 py-3 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Entité</th>
                <th className="px-4 py-3 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">IP</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-ink-muted">
                    <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-tech border-t-transparent" role="status" />
                    <p className="mt-3">Chargement…</p>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-ink-muted">
                    Aucune entrée ne correspond aux filtres.
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id} className="border-b border-border last:border-b-0 transition-colors hover:bg-surface/60">
                    <td className="whitespace-nowrap px-4 py-3 text-ink-muted tabular-nums">
                      {new Date(item.createdAt).toLocaleString('fr-FR')}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="tech" size="sm">
                        {ACTION_LABEL[item.action] ?? item.action}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      {item.userEmail ? (
                        <>
                          <div className="font-medium text-ink">
                            {item.userPrenom} {item.userNom}
                          </div>
                          <div className="text-xs text-ink-muted">{item.userEmail}</div>
                        </>
                      ) : (
                        <span className="text-ink-muted">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-ink-muted">
                      {item.entityType ? (
                        <>
                          <div className="font-mono">{item.entityType}</div>
                          {item.entityId && (
                            <div className="font-mono text-xs">{item.entityId.slice(0, 8)}…</div>
                          )}
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-ink-muted tabular-nums">
                      {item.ipAddress ?? '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && total > 0 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-3 text-sm text-ink-muted">
            <span className="tabular-nums">
              Page {page} sur {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                aria-label="Page précédente"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Page suivante"
              >
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </section>
  );
}
